import { useState, useEffect } from 'react';
import {jwtDecode} from 'jwt-decode';
import { AuthStorage } from '../services';
import defaultAvatar from '../assets/icons/avatar.svg';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = AuthStorage.getToken();
      const storedUserData = AuthStorage.getUserData();

      if (!storedToken) {
        setIsLoading(false);
        return;
      }

      // 1) Client-side expiry check via jwt-decode
      try {
        const decoded = jwtDecode(storedToken);
        const expirationMs = decoded?.exp ? decoded.exp * 1000 : 0;
        if (!expirationMs || Date.now() >= expirationMs) {
          AuthStorage.removeToken();
          setToken(null);
          setUser(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          if (typeof window !== 'undefined' && window.location && window.location.pathname !== '/login') {
            window.location.replace('/login');
          }
          return;
        }
      } catch (e) { //error in decoding token
        AuthStorage.removeToken();
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        if (typeof window !== 'undefined' && window.location && window.location.pathname !== '/login') {
          window.location.replace('/login');
        }
        return;
      }

      // If we get here, token is syntactically valid and not expired.
      if (storedUserData) {
        setToken(storedToken);
        setUser(storedUserData);
        setIsAuthenticated(true);
      } else {
        AuthStorage.removeToken();
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
      }

      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = (newToken, customerData) => {
    if (!customerData) {
      console.error('Customer data is required for login');
      return;
    }

    setToken(newToken);
    setUser(customerData);
    AuthStorage.setUserData(customerData);
    setIsAuthenticated(true);
  };

  const updateUserData = (updatedCustomerData) => {
    if (!updatedCustomerData) {
      console.error('Updated customer data is required');
      return;
    }

    setUser(updatedCustomerData);
    AuthStorage.setUserData(updatedCustomerData);
  };

  const logout = () => {
    AuthStorage.removeToken();
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    if (typeof window !== 'undefined' && window.location && window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  };

  const getUserDisplayName = () => {
    if (!user) return '';
    return `${user.firstName} ${user.lastName}`;
  };

  const getUserPicture = () => {
		// If user has an explicit picture, use it
		if (user && user.pictureUrl) {
			return user.pictureUrl;
		}

		// Generate initials avatar when no picture is available
		const firstName = user?.firstName || '';
		const lastName = user?.lastName || '';
		const firstInitial = (firstName.trim()[0] || '').toUpperCase();
		const lastInitial = (lastName.trim()[0] || '').toUpperCase();
		const initials = `${firstInitial}${lastInitial}`.trim();

		if (!initials) {
			return defaultAvatar;
		}

		// Create a simple SVG data URL with the initials centered
		const size = 128;
		const bg = '#E8E8EA';
		const fg = '#180A6399'; 
		const fontSize = 52;
		const svg = `<?xml version="1.0" encoding="UTF-8"?>
			<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
				<rect width="100%" height="100%" rx="16" ry="16" fill="${bg}"/>
				<text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Inter, system-ui, -apple-system, Plus Jakarta Sans" font-size="${fontSize}" font-weight="500" fill="${fg}">${initials}</text>
			</svg>`;
		const encoded = typeof window !== 'undefined' && window.btoa ? window.btoa(svg) : Buffer.from(svg).toString('base64');
		return `data:image/svg+xml;base64,${encoded}`;
  };

  const getUserEmail = () => {
    if (!user) return '';
    return user.email || '';
  };

  return {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    updateUserData,
    getUserDisplayName,
    getUserPicture,
    getUserEmail,
  };
};
